import { useCallback } from 'react';
import { useDelayedCallback } from './useDelayedCallback';
import { useSmartPolling } from './useSmartPolling';
import { useShowDetection } from './useShowDetection';
import { debugError, debugLog } from '../../utils/debugLog';

interface UseNewShowTimerOptions {
  /**
   * Function to fetch fresh show data and detect new shows
   * Should return true if new shows found, false to continue polling
   */
  detectNewShows?: () => Promise<boolean>;
  
  /**
   * Delay before checking for new shows after live show change
   * @default 5 minutes (300000ms)
   */
  initialDelay?: number;
  
  /**
   * Polling interval when active (tab focused)
   * @default 1 minute (60000ms)
   */
  activeInterval?: number;
  
  /**
   * Polling interval when inactive (tab not focused)
   * @default 5 minutes (300000ms)
   */
  inactiveInterval?: number;
  
  /**
   * Maximum number of polling attempts before giving up
   * @default 30
   */
  maxRetries?: number;
  
  /**
   * Enable debug logging
   * @default false
   */
  enableLogging?: boolean;
}

interface UseNewShowTimerReturn {
  /**
   * Trigger new show check after live show change
   * Sets up delayed timer followed by smart polling if needed
   */
  triggerNewShowCheck: () => void;
  
  /**
   * Stop all polling and timers
   */
  stopAllTimers: () => void;
  
  /**
   * Check if currently polling for new shows
   */
  isPolling: boolean;
}

/**
 * Focused hook for new show detection timer functionality
 * 
 * This hook provides timer-based new show detection without data fetching.
 * It coordinates delayed callbacks and smart polling to detect when new
 * shows become available after a live show change.
 * 
 * Features:
 * - 5-minute initial delay after live show change
 * - Smart polling with exponential backoff if no shows found
 * - Activity-based polling intervals (faster when tab active)
 * - Circuit breaker pattern with retry limits
 * - Proper cleanup and memory leak prevention
 * 
 * Usage:
 * ```typescript
 * const timer = useNewShowTimer({
 *   onNewShowsDetected: (shows) => {
 *     // Handle new shows found
 *   }
 * });
 * 
 * // Trigger when live show changes
 * timer.triggerNewShowCheck();
 * ```
 * 
 * @param options Configuration options for timer behavior
 * @returns Timer control functions and state
 */
export const useNewShowTimer = (
  options: UseNewShowTimerOptions = {}
): UseNewShowTimerReturn => {
  const {
    detectNewShows,
    initialDelay = 5 * 60 * 1000, // 5 minutes
    activeInterval = 60 * 1000,   // 1 minute
    inactiveInterval = 5 * 60 * 1000, // 5 minutes
    maxRetries = 30,
    enableLogging = false,
  } = options;

  // Use focused timer hooks
  const showDetection = useShowDetection({
    todayOnly: true,
    enableLogging,
  });
  const delayedCallback = useDelayedCallback({ 
    defaultDelay: initialDelay 
  });

  // Smart polling for continuous checking when no new shows found
  const smartPolling = useSmartPolling(
    async () => {
      if (enableLogging) {
        debugLog('🔍 New show timer: Checking for new shows...');
      }
      
      try {
        // Use provided detection function if available
        if (detectNewShows) {
          const foundNewShows = await detectNewShows();
          return foundNewShows;
        }
        
        // Default behavior: return false to continue polling
        return false;
      } catch (error) {
        if (enableLogging) {
          debugError('New show timer: Error during detection:', error);
        }
        return false; // Continue polling on error
      }
    },
    {
      activeInterval,
      inactiveInterval,
      maxRetries,
    }
  );

  // Function to trigger new show check (called when live show changes)
  const triggerNewShowCheck = useCallback(() => {
    if (enableLogging) {
      debugLog('🎵 New show timer: Live show changed! Setting timer for new show check...');
    }
    
    // Stop any ongoing polling
    smartPolling.stopPolling();

    // Reset detection baseline before scheduling follow-up checks
    showDetection.updateBaseline([]);
    
    // Schedule new show check after initial delay
    delayedCallback.scheduleCallback(() => {
      if (enableLogging) {
        debugLog(`⏰ New show timer: ${initialDelay / 1000}s elapsed, starting detection...`);
      }
      
      try {
        // Start smart polling - the actual detection logic will be provided by consumer
        smartPolling.startPolling();
      } catch (error) {
        if (enableLogging) {
          debugError('New show timer: Error starting polling:', error);
        }
      }
    });
  }, [smartPolling, delayedCallback, initialDelay, enableLogging, showDetection]);

  // Stop all timers
  const stopAllTimers = useCallback(() => {
    smartPolling.stopPolling();
    delayedCallback.cancelCallback();
    showDetection.clearBaseline?.();
  }, [smartPolling, delayedCallback, showDetection]);

  return {
    triggerNewShowCheck,
    stopAllTimers,
    isPolling: smartPolling.state.isPolling,
  };
};
